/**
 * Game cues: lasers, pulses, filtered noise. Sci-fi timbres are fine;
 * no slot-machine bells and no third-party samples.
 */

type NoiseColor = 'white' | 'pink'

const MASTER_GAIN = 0.55

export class SoftSynth {
  private ctx: AudioContext | null = null
  private master: GainNode | null = null
  private reverb: ConvolverNode | null = null
  private wet: GainNode | null = null
  private userGain = 1

  unlock(): AudioContext | null {
    const Ctor =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
    if (!Ctor) return null
    if (!this.ctx) {
      this.ctx = new Ctor()
      this.buildGraph()
    }
    if (this.ctx.state === 'suspended') void this.ctx.resume()
    return this.ctx
  }

  /** Master bus so sample playback shares mute / compressor with the synth. */
  output(): AudioNode | null {
    return this.master
  }

  /**
   * Linear 0–1 multiplier on the synth master. Samples use their own gain.
   */
  setUserGain(value: number): void {
    this.userGain = Math.max(0, Math.min(1, Number.isFinite(value) ? value : 1))
    this.applyMaster()
  }

  isReady(): boolean {
    return !!this.ctx && this.ctx.state === 'running'
  }

  private buildGraph(): void {
    if (!this.ctx) return
    const comp = this.ctx.createDynamicsCompressor()
    comp.threshold.value = -18
    comp.knee.value = 18
    comp.ratio.value = 4
    comp.attack.value = 0.004
    comp.release.value = 0.18
    this.master = this.ctx.createGain()
    this.applyMaster()
    this.reverb = this.ctx.createConvolver()
    this.reverb.buffer = this.makeImpulse(0.7, 3.2)
    this.wet = this.ctx.createGain()
    this.wet.gain.value = 0.14
    this.reverb.connect(this.wet)
    this.wet.connect(comp)
    this.master.connect(comp)
    comp.connect(this.ctx.destination)
  }

  private applyMaster(): void {
    if (this.master) this.master.gain.value = MASTER_GAIN * this.userGain
  }

  private makeImpulse(seconds: number, decay: number): AudioBuffer {
    const ctx = this.ctx!
    const len = Math.floor(ctx.sampleRate * seconds)
    const buffer = ctx.createBuffer(2, len, ctx.sampleRate)
    for (let ch = 0; ch < 2; ch++) {
      const data = buffer.getChannelData(ch)
      for (let i = 0; i < len; i++) {
        data[i] = (Math.random() * 2 - 1) * (1 - i / len) ** decay
      }
    }
    return buffer
  }

  now(): number {
    return this.ctx?.currentTime ?? 0
  }

  private dest(): AudioNode | null {
    return this.master
  }

  private tapReverb(node: AudioNode, amount: number): void {
    if (!this.reverb) return
    const send = this.ctx!.createGain()
    send.gain.value = amount
    node.connect(send)
    send.connect(this.reverb)
  }

  /**
   * Soft sine (optional 2nd harmonic) with a clean ADS-style envelope.
   */
  tone(
    freq: number,
    when: number,
    dur: number,
    vol: number,
    opts?: {
      harmonic?: number
      attack?: number
      reverb?: number
      type?: OscillatorType
    },
  ): void {
    if (!this.ctx || !this.dest()) return
    const attack = opts?.attack ?? 0.012
    const osc = this.ctx.createOscillator()
    osc.type = opts?.type ?? 'sine'
    osc.frequency.setValueAtTime(freq, when)
    const env = this.ctx.createGain()
    env.gain.setValueAtTime(0.0001, when)
    env.gain.exponentialRampToValueAtTime(vol, when + attack)
    env.gain.exponentialRampToValueAtTime(0.0001, when + dur)
    osc.connect(env)
    env.connect(this.dest()!)
    this.tapReverb(env, opts?.reverb ?? 0.15)
    osc.start(when)
    osc.stop(when + dur + 0.03)
    if (opts?.harmonic) {
      this.tone(freq * 2, when, dur * 0.7, vol * opts.harmonic, {
        attack,
        type: opts.type,
        reverb: (opts.reverb ?? 0.15) * 0.6,
      })
    }
  }

  sweep(
    from: number,
    to: number,
    when: number,
    dur: number,
    vol: number,
    reverb = 0.12,
    type: OscillatorType = 'sine',
  ): void {
    if (!this.ctx || !this.dest()) return
    const osc = this.ctx.createOscillator()
    osc.type = type
    osc.frequency.setValueAtTime(from, when)
    osc.frequency.exponentialRampToValueAtTime(Math.max(1, to), when + dur)
    const env = this.ctx.createGain()
    env.gain.setValueAtTime(0.0001, when)
    env.gain.exponentialRampToValueAtTime(vol, when + 0.02)
    env.gain.exponentialRampToValueAtTime(0.0001, when + dur)
    const filter = this.ctx.createBiquadFilter()
    filter.type = 'lowpass'
    filter.frequency.setValueAtTime(Math.max(from, to) * 2.2, when)
    osc.connect(filter)
    filter.connect(env)
    env.connect(this.dest()!)
    this.tapReverb(env, reverb)
    osc.start(when)
    osc.stop(when + dur + 0.03)
  }

  /** Sci-fi pass: saw into a closing filter. */
  laser(from: number, to: number, when: number, dur: number, vol: number): void {
    this.sweep(from, to, when, dur, vol, 0.08, 'sawtooth')
  }

  whoosh(
    when: number,
    dur: number,
    vol: number,
    fromHz: number,
    toHz: number,
  ): void {
    this.noise(when, dur, vol, {
      color: 'pink',
      filter: 'bandpass',
      freq: fromHz,
      sweepTo: toHz,
      q: 1.1,
    })
  }

  noise(
    when: number,
    dur: number,
    vol: number,
    opts: {
      color: NoiseColor
      filter: BiquadFilterType
      freq: number
      sweepTo?: number
      q?: number
    },
  ): void {
    if (!this.ctx || !this.dest()) return
    const n = Math.max(1, Math.floor(this.ctx.sampleRate * dur))
    const buffer = this.ctx.createBuffer(1, n, this.ctx.sampleRate)
    const data = buffer.getChannelData(0)
    let b0 = 0
    let b1 = 0
    let b2 = 0
    for (let i = 0; i < n; i++) {
      const white = Math.random() * 2 - 1
      if (opts.color === 'pink') {
        b0 = 0.99765 * b0 + white * 0.099046
        b1 = 0.963 * b1 + white * 0.2965164
        b2 = 0.57 * b2 + white * 1.052691
        data[i] = b0 + b1 + b2 + white * 0.1848
      } else {
        data[i] = white
      }
    }
    const src = this.ctx.createBufferSource()
    src.buffer = buffer
    const filter = this.ctx.createBiquadFilter()
    filter.type = opts.filter
    filter.Q.value = opts.q ?? 0.9
    filter.frequency.setValueAtTime(opts.freq, when)
    if (opts.sweepTo) {
      filter.frequency.exponentialRampToValueAtTime(
        Math.max(40, opts.sweepTo),
        when + dur,
      )
    }
    const env = this.ctx.createGain()
    env.gain.setValueAtTime(0.0001, when)
    env.gain.exponentialRampToValueAtTime(vol, when + 0.01)
    env.gain.exponentialRampToValueAtTime(0.0001, when + dur)
    src.connect(filter)
    filter.connect(env)
    env.connect(this.dest()!)
    this.tapReverb(env, 0.1)
    src.start(when)
    src.stop(when + dur + 0.02)
  }

  /** Airy sparkle — filtered noise, not a pitched chime. */
  shimmer(when: number, dur: number, vol: number): void {
    this.noise(when, dur, vol, {
      color: 'pink',
      filter: 'highpass',
      freq: 2400,
      sweepTo: 6200,
      q: 0.55,
    })
  }

  thud(when: number, vol: number): void {
    this.sweep(140, 48, when, 0.16, vol, 0.08)
    this.noise(when, 0.06, vol * 0.35, {
      color: 'pink',
      filter: 'lowpass',
      freq: 400,
      q: 0.7,
    })
  }
}

export const synth = new SoftSynth()
